import React from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { ActionCausedByRole, HistoryEvent } from '@app/api';
import { EventHistoryRow } from './EventHistoryRow';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    borderCollapse: 'separate',
    borderSpacing: `${theme.marginBase}px`,
    border: 'none',
  },
}));

export interface HistoryEventDisplayConfigItem<EventType extends HistoryEvent> {
  icon?: string;
  anonymousCausedBy?: ActionCausedByRole;
  getDetails?: (event: EventType, t: TFunction) => string | React.ReactNode;
  getContext?: (event: EventType) => string | undefined;
  rightChildren?: (event: EventType) => React.ReactNode;
}

interface Props<EventType extends HistoryEvent> {
  className?: string;
  events: EventType[];
  eventsConfig?: Record<
    string,
    HistoryEventDisplayConfigItem<EventType> | string
  >;
  overGray?: boolean;
}

export const EventHistory = <EventType extends HistoryEvent>({
  events,
  className,
  eventsConfig,
  overGray,
}: Props<EventType>) => {
  const classes = useStyles();

  const { t } = useTranslation();

  return (
    <table className={classNames(className, classes.container)}>
      {events.map((event: any) => {
        const eventConfig = eventsConfig?.[event.eventName];
        const icon =
          typeof eventConfig === 'string' ? eventConfig : eventConfig?.icon;
        const config: HistoryEventDisplayConfigItem<EventType> =
          typeof eventConfig === 'string' ? {} : eventConfig || {};

        return (
          <EventHistoryRow
            key={event.id}
            event={event}
            icon={icon}
            rightChildren={
              !!config.rightChildren && config.rightChildren(event)
            }
            anonymousCausedBy={config.anonymousCausedBy}
            details={config.getDetails && config.getDetails(event, t)}
            context={config.getContext && config.getContext(event)}
            overGray={overGray}
          />
        );
      })}
    </table>
  );
};
